import {
    DropDown,
    Input,
    toInt,
    useGridFilterUrlContext,
    ValidatedDropDownList,
} from '@finbeat/react-toolkit';
import { BODatePicker } from 'base-components/BODatePicker/BODatePicker';
import { PaymentFilter } from 'models/api/payment';
import { ChangeEvent } from 'react';
import { Col, Row } from 'reactstrap';
import { t } from 'i18next';

export type PaymentsFilterProps = {
    onFilterShow?: () => void;
    paymentSources: DropDown[];
    statuses: DropDown[];
    businessBrandCode: DropDown[];
};

const ignoredPeriodProps: Record<keyof PaymentFilter, boolean> = {
    sd: false,
    ed: false,
    sourceId: false,
    status: false,
    referenceId: true,
    orderId: true,
    isIgnorePeriod: false,
    orderColumn: false,
    orderDesc: false,
    pageNum: false,
    pageSize: false,
    businessBrandCode: false,
};

const PaymentsFilter = ({ paymentSources, statuses, businessBrandCode }: PaymentsFilterProps) => {
    const { draftFilter, setDraftFilter } = useGridFilterUrlContext<PaymentFilter>();

    const changeInputProp = (e: ChangeEvent<HTMLInputElement>) =>
        changeProp(e.target.value, e.target.name as keyof PaymentFilter);

    const changeProp = (value: string | DropDown, name: keyof PaymentFilter) => {
        const updatedValue = { [name]: value } as PaymentFilter;

        setDraftFilter((prev) => ({
            ...prev,
            ...updatedValue,
            isIgnorePeriod: getIsIgnoredPeriod(updatedValue, name),
        }));
    };

    const getIsIgnoredPeriod = (updatedValue: PaymentFilter, name: keyof PaymentFilter) => {
        let isIgnorePeriod = false;
        if (ignoredPeriodProps[name])
            isIgnorePeriod =
                Object.entries(updatedValue).filter(
                    ([key, value]) => ignoredPeriodProps[key as keyof PaymentFilter] && value
                ).length > 0;

        return isIgnorePeriod;
    };

    const { sd, ed, status, sourceId, orderId, referenceId } = draftFilter;

    return (
        <>
            <Row>
                <Col md={6}>
                    <BODatePicker
                        value={sd}
                        onChange={(val) =>
                            setDraftFilter((prevState) => ({ ...prevState, sd: val }))
                        }
                        inputProps={{ label: t('BackOfficeDtFrom') }}
                    />
                </Col>
                <Col md={6}>
                    <BODatePicker
                        value={ed}
                        onChange={(val) =>
                            setDraftFilter((prevState) => ({ ...prevState, ed: val }))
                        }
                        inputProps={{ label: t('BackOfficeTo') }}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <Input
                        id="p-status"
                        name="status"
                        type="select"
                        disabled={!statuses.length}
                        value={status}
                        onChange={(e) =>
                            setDraftFilter((prevState) => ({
                                ...prevState,
                                status: toInt(e.target.value),
                            }))
                        }
                        options={[<option key="-1" value=""></option>].concat(
                            statuses.map((i) => (
                                <option value={i.id ?? undefined} key={i.id}>
                                    {i.name}
                                </option>
                            ))
                        )}
                        label={t('BackOfficeStatus')}
                    />
                </Col>
                <Col>
                    <Input
                        id="p-sourceId"
                        name="sourceId"
                        type="select"
                        disabled={!paymentSources.length}
                        value={sourceId}
                        onChange={(e) =>
                            setDraftFilter((prevState) => ({
                                ...prevState,
                                sourceId: toInt(e.target.value),
                            }))
                        }
                        options={[<option key="-1" value=""></option>].concat(
                            paymentSources.map((i) => (
                                <option value={i.id ?? undefined} key={i.id}>
                                    {i.name}
                                </option>
                            ))
                        )}
                        label={t('BackOfficeSource')}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <Input
                        id="p-referenceId"
                        name="referenceId"
                        value={referenceId}
                        onChange={changeInputProp}
                        label={t('BackOfficeExternalCode')}
                    />
                </Col>
                <Col>
                    <Input
                        id="p-orderId"
                        name="orderId"
                        value={orderId}
                        onChange={changeInputProp}
                        label={t('BackOfficeAppId')}
                    />
                </Col>
            </Row>
            <Row>
                <ValidatedDropDownList<PaymentFilter>
                    name="businessBrandCode"
                    label={t('BackOfficeBrandCode')}
                    items={businessBrandCode || []}
                />
            </Row>
        </>
    );
};

export default PaymentsFilter;
